"use client"

import { useCallback, useEffect, useState } from "react"
import { motion } from "framer-motion"
import { useRouter, useSearchParams } from "next/navigation"
import { Poppins } from "next/font/google"

import { useCurrentBranch } from "@/context/current-branch-provider"
import { Reservation, ReservationFormProps } from "@/types/popadvise"
import { Card, CardContent } from "@/components/ui/card"
import { ReservationsDate } from "@/components/popadvise/reservations/reservation-date"
import { ReservationsHours } from "@/components/popadvise/reservations/reservation-hours"
import { ReservationOcassion } from "@/components/popadvise/reservations/reservation-occasion"
import { ReservationUserInfo } from "@/components/popadvise/reservations/reservation-user-info"
import { ReservationResult } from "@/components/popadvise/reservations/reservation-result"

const poppins = Poppins({
    subsets: ['latin'],
    weight: ['400', '500', '600', '700'],
})

export function Pop1SimpleReservationLayout() {

    const router = useRouter()
    const { currentBranch } = useCurrentBranch()
    const data = useSearchParams()
    const [userData, setUserData] = useState({
        date: data.get("date"),
        persons: data.get("persons") as ReservationFormProps["persons"],
        hour: data.get("hour") as ReservationFormProps["hour"],
        branch: data.get("branch") || currentBranch.id,
        occasion: undefined,
        specialRequest: "",
        countryCode: "52",
        phone: "",
        name: "",
        lastName: "",
        email: "",
    })

    const [step, setStep] = useState(0)
    const [reservationResult, setReservationResult] = useState<Reservation | null>(null)

    const redirectToHome = useCallback(() => {
        if (userData.branch === null) {
            router.push("/")
        }
    }, [userData.branch, router])

    useEffect(() => {
        redirectToHome()
    }, [redirectToHome])

    return (
        <div className={`w-full min-h-[80vh] mx-auto pt-16 md:pt-40 pb-10 ${step === 5 ? 'sm:max-w-[630px]' : 'sm:max-w-[500px]'}`}>
            <Card className="border-0 dark:bg-transparent mx-auto">
                <CardContent className={`p-0 sm:rounded-3xl  ${poppins.className}`}>
                    {
                        step === 0 && (
                            <motion.div
                                key="reservationDate"
                                initial={{ opacity: 0, scale: 0.95 }}
                                animate={{ opacity: 1, scale: 1 }}
                                exit={{ opacity: 0, scale: 0.95, x: -100 }}
                                transition={{ duration: 0.5, ease: "easeInOut", delay: 0.05 }}
                            >
                                <ReservationsDate onContinue={() => setStep(step + 1)} userData={userData} setUserData={setUserData} />
                            </motion.div>
                        )
                    }
                    {
                        step === 1 && (
                            <motion.div
                                key="reservationHour"
                                initial={{ opacity: 0, scale: 0.95 }}
                                animate={{ opacity: 1, scale: 1 }}
                                exit={{ opacity: 0, scale: 0.95, x: -100 }}
                                transition={{ duration: 0.5, ease: "easeInOut", delay: 0.05 }}
                            >
                                <ReservationsHours
                                    onContinue={() => setStep(step + 1)}
                                    onBackStep={() => setStep(step - 1)}
                                    userData={userData}
                                    setUserData={setUserData}
                                />
                            </motion.div>
                        )
                    }
                    {
                        step === 2 && (
                            <motion.div
                                key="reservationOcassion"
                                initial={{ opacity: 0, scale: 0.95 }}
                                animate={{ opacity: 1, scale: 1 }}
                                exit={{ opacity: 0, scale: 0.95, x: -100 }}
                                transition={{ duration: 0.5, ease: "easeInOut", delay: 0.05 }}
                            >
                                <ReservationOcassion
                                    onContinue={() => setStep(step + 1)}
                                    onBackStep={() => setStep(step - 1)}
                                    userData={userData}
                                    setUserData={setUserData}
                                />
                            </motion.div>
                        )
                    }
                    {
                        step === 3 && (
                            <motion.div
                                key="reservationUserInfo"
                                initial={{ opacity: 0, scale: 0.95 }}
                                animate={{ opacity: 1, scale: 1 }}
                                exit={{ opacity: 0, scale: 0.95, x: -100 }}
                                transition={{ duration: 0.5, ease: "easeInOut", delay: 0.05 }}
                            >
                                <ReservationUserInfo
                                    onContinue={() => setStep(step + 1)}
                                    onBackStep={() => setStep(step - 1)}
                                    userData={userData}
                                    setUserData={setUserData}
                                    setResult={setReservationResult}
                                />
                            </motion.div>
                        )
                    }
                    {
                        step === 4 && (
                            <motion.div
                                key="reservationResult"
                                initial={{ opacity: 0, scale: 0.95 }}
                                animate={{ opacity: 1, scale: 1 }}
                                exit={{ opacity: 0, scale: 0.95, x: -100 }}
                                transition={{ duration: 0.5, ease: "easeInOut", delay: 0.05 }}
                            >
                                <ReservationResult reservation={reservationResult} />
                            </motion.div>
                        )
                    }
                </CardContent>
            </Card>
        </div>
    )
}